import React, { useEffect, useRef, useState } from 'react'
import Head from 'next/head'
import Link from 'next/link'
import logger from '../../services/logger'
import styled from '@emotion/styled'
import { mq } from '../../helpers'
import { gsColors as c } from '../../styles/Variables'
import FaqAccordion from '../../components/FAQ/FaqAccordion'
import { fetchAllCategories } from '../../services/faqStrapi'

export async function getStaticProps() {
  logger.log('FAQ.getStaticProps: fetching categories from Strapi')
  try {
    const categories = await fetchAllCategories()

    const categoriesWithCounts = await Promise.all(
      categories.map(async (category) => {
        const categoryId = category.documentId || category.id
        const countRes = await fetch(
          `${process.env.STRAPI_URL}/api/faqs?filters[category][documentId][$eq]=${encodeURIComponent(categoryId)}&pagination[pageSize]=1`,
          { headers: { Authorization: `Bearer ${process.env.STRAPI_API_KEY}` } }
        )
        const countResponse = await countRes.json()
        const faqCount = countResponse.meta?.pagination?.total || 0
        return { ...category, faqs: new Array(faqCount) }
      })
    )

    const categoriesWithFaqs = categoriesWithCounts.filter((cat) => cat.faqs.length > 0)
    return { props: { categories: categoriesWithFaqs }, revalidate: 60 }
  } catch (error) {
    logger.error('FAQ.getStaticProps: error fetching categories from Strapi', { error })
    return { props: { categories: [] }, revalidate: 60 }
  }
}

export default function FAQPage({ categories }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState(null)
  const [isSearching, setIsSearching] = useState(false)
  const debounceRef = useRef(null)

  useEffect(() => {
    const trimmed = query.trim()
    if (debounceRef.current) { clearTimeout(debounceRef.current) }

    if (trimmed.length < 2) {
      setResults(null)
      setIsSearching(false)
      return
    }

    const controller = new AbortController()
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const res = await fetch(`/api/faq/search?q=${encodeURIComponent(trimmed)}`, { signal: controller.signal })
        const data = await res.json()
        setResults(data.results || [])
      } catch (error) {
        if (error.name === 'AbortError') { return }
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) { clearTimeout(debounceRef.current) }
      controller.abort()
    }
  }, [query])

  const showingResults = results !== null

  return (
    <div>
      <Head>
        <title>Frequently Asked Questions - Glamsquad</title>
        <meta name="title" content="Frequently Asked Questions - Glamsquad" />
        <meta name="description" content="Answers to common questions about GLAMSQUAD's in-home hair, makeup, and nail services — booking, pricing, service areas, cancellations, and more." />
        <link rel="canonical" href="https://glamsquad.com/faq" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Frequently Asked Questions - Glamsquad" />
        <meta property="og:description" content="Answers to common questions about GLAMSQUAD's in-home hair, makeup, and nail services — booking, pricing, service areas, cancellations, and more." />
        <meta property="og:url" content="https://glamsquad.com/faq" />
      </Head>
      <FAQPageWrapper>
        <div className='header-container'>
          <h1>FAQs</h1>
        </div>

        <div className='search-container'>
          <div className='search-input-wrap'>
            <input
              type='search'
              className='faq-search'
              placeholder='Search FAQs…'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label='Search FAQs'
              aria-busy={isSearching}
            />
            {isSearching && <span className='search-spinner' aria-hidden='true' />}
          </div>
        </div>

        {showingResults ? (
          <div className='search-results' aria-live='polite'>
            {!isSearching && results.length === 0 && (
              <p className='search-status'>No results found for &ldquo;{query.trim()}&rdquo;.</p>
            )}
            {!isSearching && results.length > 0 && <FaqAccordion faqs={results} />}
          </div>
        ) : (
          <div className='faq-grid'>
            {categories.map((category) => {
              const categoryName = category.name || category.title
              const categorySlug = category.slug || category.documentId
              const faqCount = category.faqs?.length || 0
              if (!categoryName) { return null }

              return (
                <Link key={category.id} href={`/faq/category/${categorySlug}`} className='faq-card'>
                  <h3 className='faq-question'>{categoryName}</h3>
                  <p className='faq-count'>{faqCount} {faqCount === 1 ? 'article' : 'articles'}</p>
                </Link>
              )
            })}
          </div>
        )}
      </FAQPageWrapper>
    </div>
  )
}

const FAQPageWrapper = styled.div`
  width: 100vw;
  max-width: 1660px;
  margin: 0 auto;

  .header-container {
    padding: 103px 88px 40px 88px;
    ${mq('max', '800px')} {
      padding: 30px 16px 20px 16px;
    }
    h1 {
      color: var(--Black, #000);
      font-family: Garnett;
      font-size: 82px;
      font-weight: 750;
      line-height: 138%;
      letter-spacing: 1.64px;
      text-transform: uppercase;
      text-align: center;

      ${mq('max', '800px')} {
        font-size: 28px;
        text-align: left;
      }
    }
  }

  .search-container {
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    padding: 0 16px 40px 16px;
    ${mq('max', '800px')} {
      padding: 0 16px 24px 16px;
    }
  }

  .search-input-wrap {
    position: relative;
  }

  .faq-search {
    width: 100%;
    padding: 18px 52px 18px 24px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-family: Euclid;
    font-size: 18px;
    box-sizing: border-box;

    &:focus {
      outline: 2px solid ${c.cobalt};
      outline-offset: 2px;
      border-color: ${c.cobalt};
    }
  }

  .search-spinner {
    position: absolute;
    right: 20px;
    top: calc(50% - 9px);
    width: 18px;
    height: 18px;
    border: 2px solid #e0e0e0;
    border-top-color: ${c.cobalt};
    border-radius: 50%;
    animation: faq-search-spin 0.6s linear infinite;
  }

  @keyframes faq-search-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .search-results {
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    padding: 0 16px 80px 16px;
    ${mq('max', '800px')} {
      padding: 0 16px 40px 16px;
    }
  }

  .search-status {
    font-family: Euclid;
    font-size: 16px;
    color: #666;
  }

  .faq-grid {
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    padding: 0 16px 80px 16px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;

    ${mq('max', '800px')} {
      grid-template-columns: 1fr;
      gap: 16px;
      padding: 0 16px 40px 16px;
    }
  }

  .faq-card {
    background-color: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 32px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    text-decoration: none;
    display: block;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      border-color: #d0d0d0;
    }

    ${mq('max', '800px')} {
      padding: 24px;
    }
  }

  .faq-question {
    color: #000;
    font-family: Garnett;
    font-size: 20px;
    font-weight: 600;
    line-height: 140%;
    margin: 0 0 8px 0;

    ${mq('max', '800px')} {
      font-size: 18px;
    }
  }

  .faq-count {
    color: #666;
    font-family: Euclid;
    font-size: 14px;
    margin: 0;
  }
`
